/**
 * A UX hint only: persisted across reloads, TTL-bounded so a lost webhook
 * cannot hide Upgrade forever.
 */
import { create } from "zustand";
import {
  fetchBillingContext,
  fetchCurrentPlan,
  type BillingContext,
} from "@/lib/api/organizations";
import { useNotificationStore } from "./useNotificationStore";

const PENDING_CHECKOUT_TTL_MS = 15 * 60 * 1000;

const STORAGE_KEY = "mycorr.pendingCheckout";

type PendingByOrg = Record<string, number>;

interface PendingCheckoutStore {
  startedAt: PendingByOrg;
  /// Set when a confirmation saw the period move; Plan & usage re-reads on it.
  confirmedAt: PendingByOrg;
  cardChangedAt: PendingByOrg;
  confirming: Record<string, true>;
  cardConfirming: Record<string, true>;
  markPending: (orgId: string) => void;
  clearPending: (orgId: string) => void;
  markConfirmed: (orgId: string) => void;
  markCardChanged: (orgId: string) => void;
}

// localStorage can be absent, full or blocked; none of those may break the page.
function load(): PendingByOrg {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number",
      ),
    );
  } catch {
    return {};
  }
}

function save(startedAt: PendingByOrg): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(startedAt));
  } catch {
    // In-memory state still serves this tab.
  }
}

export const usePendingCheckoutStore = create<PendingCheckoutStore>((set) => ({
  startedAt: load(),
  confirmedAt: {},
  cardChangedAt: {},
  confirming: {},
  cardConfirming: {},
  markConfirmed: (orgId) =>
    set((state) => ({
      confirmedAt: { ...state.confirmedAt, [orgId]: Date.now() },
    })),
  markCardChanged: (orgId) =>
    set((state) => ({
      cardChangedAt: { ...state.cardChangedAt, [orgId]: Date.now() },
    })),
  markPending: (orgId) =>
    set((state) => {
      const startedAt = { ...state.startedAt, [orgId]: Date.now() };
      save(startedAt);
      return { startedAt };
    }),
  clearPending: (orgId) =>
    set((state) => {
      if (!(orgId in state.startedAt)) return state;
      const { [orgId]: _dropped, ...startedAt } = state.startedAt;
      save(startedAt);
      return { startedAt };
    }),
}));

function isCheckoutPending(
  startedAt: number | undefined,
  now = Date.now(),
): boolean {
  return startedAt !== undefined && now - startedAt < PENDING_CHECKOUT_TTL_MS;
}

export function useIsCheckoutPending(orgId: string): boolean {
  const startedAt = usePendingCheckoutStore((s) => s.startedAt[orgId]);
  return isCheckoutPending(startedAt);
}

const CONFIRM_FIRST_INTERVAL_MS = 400;
const CONFIRM_MAX_INTERVAL_MS = 2_000;
export const CONFIRM_WINDOW_MS = 30_000;
/// A card change arrives as a `transaction.completed` delivery, which can take
/// well over the plan window.
export const CARD_CONFIRM_WINDOW_MS = 120_000;

type InFlight = "confirming" | "cardConfirming";

/// `false` when a run for `orgId` is already in flight.
function begin(key: InFlight, orgId: string): boolean {
  const store = usePendingCheckoutStore;
  if (store.getState()[key][orgId]) return false;
  store.setState((s) => ({ [key]: { ...s[key], [orgId]: true } }));
  return true;
}

function end(key: InFlight, orgId: string): void {
  usePendingCheckoutStore.setState((s) => {
    const { [orgId]: _done, ...rest } = s[key];
    return { [key]: rest };
  });
}

/// `paid_by` is in it because a takeover changes who pays before it changes
/// which card.
const cardFingerprint = (billing: BillingContext | null): string =>
  JSON.stringify([
    billing?.card_on_file ?? null,
    billing?.card ?? null,
    billing?.paid_by ?? null,
  ]);

/// `moved` carries the success sentence.
type Probe = () => Promise<"same" | "failed" | { moved: string }>;

interface ConfirmRun {
  key: InFlight;
  windowMs: number;
  onBegin?: () => void;
  probe: Probe;
  again: () => void;
  opening: string;
  /// The end of the window: nothing answered, respectively answered but unmoved.
  unreachable: string;
  slow: string;
}

/// One run per org and key at a time.
function confirm(orgId: string, run: ConfirmRun): void {
  if (!begin(run.key, orgId)) return;
  run.onBegin?.();

  const notify = useNotificationStore.getState().post;
  const again = { label: "Check again", onClick: run.again };
  notify({ message: run.opening, variant: "default", busy: true });

  const deadline = Date.now() + run.windowMs;
  let wait = CONFIRM_FIRST_INTERVAL_MS;
  let polls = 0;
  let failures = 0;

  const finish = (entry: Parameters<typeof notify>[0]): void => {
    end(run.key, orgId);
    notify(entry);
  };

  const poll = async (): Promise<void> => {
    polls += 1;
    const outcome = await run.probe();
    if (outcome === "failed") {
      failures += 1;
    } else if (outcome !== "same") {
      finish({ message: outcome.moved, variant: "success" });
      return;
    }
    if (Date.now() >= deadline) {
      // Only a window in which nothing was answered reads as unreachable.
      finish({
        message: failures === polls ? run.unreachable : run.slow,
        variant: failures === polls ? "error" : "warning",
        persistent: true,
        action: again,
      });
      return;
    }
    window.setTimeout(() => void poll(), wait);
    wait = Math.min(wait * 2, CONFIRM_MAX_INTERVAL_MS);
  };

  void poll();
}

/// Polls until the org's plan code moves off `from`.
export function confirmPlanChange(orgId: string, from: string): void {
  confirm(orgId, {
    key: "confirming",
    windowMs: CONFIRM_WINDOW_MS,
    onBegin: () => usePendingCheckoutStore.getState().markPending(orgId),
    probe: async () => {
      try {
        const plan = await fetchCurrentPlan(orgId);
        if (!plan || plan.code === from) return "same";
        usePendingCheckoutStore.getState().clearPending(orgId);
        usePendingCheckoutStore.getState().markConfirmed(orgId);
        return {
          moved: `You’re on ${plan.name}. Its limits apply to this organization right away.`,
        };
      } catch {
        return "failed";
      }
    },
    again: () => confirmPlanChange(orgId, from),
    opening: "Payment received — setting up your new plan…",
    unreachable:
      "Payment received. We couldn’t reach the server to confirm your new plan — your payment is safe.",
    slow: "Payment received. Your new plan is taking longer than usual to activate.",
  });
}

/// Polls until the billing context moves away from `before` — only the webhook
/// names the new payer. `orgName` is for the takeover sentence.
export function confirmCardChange(
  orgId: string,
  before: BillingContext | null,
  orgName: string,
): void {
  const beforePrint = cardFingerprint(before);
  const wasMine = before?.paid_by?.is_you === true;
  // A card saved against a past-due subscription is two acts: the card lands,
  // then Paddle charges what is owed. Settling on the card alone re-reads the
  // panel while the dunning callout is still true, and nothing reads again.
  const wasPastDue = before?.subscription?.past_due_since != null;
  confirm(orgId, {
    key: "cardConfirming",
    windowMs: CARD_CONFIRM_WINDOW_MS,
    probe: async () => {
      const context = await fetchBillingContext(orgId);
      if (!context) return "failed";
      if (cardFingerprint(context) === beforePrint) return "same";
      if (wasPastDue && context.subscription?.past_due_since != null)
        return "same";
      usePendingCheckoutStore.getState().markCardChanged(orgId);
      return {
        moved:
          !wasMine && context.paid_by?.is_you
            ? `Card updated. Your card now pays for ${orgName}.`
            : "Card updated.",
      };
    },
    again: () => confirmCardChange(orgId, before, orgName),
    opening: "Card saved — confirming with the payment processor…",
    unreachable:
      "Card saved. We couldn’t reach the server to confirm the change — the card is safe at the payment processor.",
    slow: "Card saved at the payment processor. This page hasn’t caught up with it yet.",
  });
}
